import * as React from 'react';
import MainSectionLayout from '../layout/MainSectionLayout';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import {
  ApplicationStore,
  AuthenticationStore,
  BankHolidayStore,
  BankHolidayDateStore,
  GroupStore,
  PharmacyAnnualReviewStore,
  PharmacyDetailedStore,
  PharmacyStore,
  SecretariatStore,
  SystemStatusStore,
} from '../../store';
import { inject, observer } from 'mobx-react';
import styled from '@emotion/styled';
import SecretariatSelection from '../common/dropdownSelection/SecretariatSelection';
import { Secretariat } from '../../store/models/Secretariat';
import { css } from 'emotion';
import { Card, Tab, Tabs, Navbar } from '@blueprintjs/core';
import { TranslatedMessage } from '../common/translation';
import { SystemStatusSubSystem } from '../../common/enums/SystemStatusSubSystem';
import { SystemStatus } from '../../store/models/SystemStatus';
import BlackPointManager from './BlackPointManager';
import BankHolidayDateManager from './BankHolidayDateManager';
import DutyUpload from './DutyUpload';
import ExchangeManager from './ExchangeManager';
import PlanningManager from './PlanningManager';
import AnnualReviewManager from './AnnualReviewManager';
import PrefManager from './PrefManager';
import DutyDownload from './DutyDownload';
import moment from 'moment';
import { PharmacyAnnualReview } from '../../store/models/PharmacyAnnualReview';
import { PharmacyAnnualReviewSearchCriteria } from '../../store/PharmacyAnnualReviewStore';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Group } from '../../store/models/Group';
import MissingBlackPointManager from './MissingBlackPointManager';
import MessageTab from './MessageTab';
import { Message } from '../../store/models/Message';
import { MessageStore } from '../../store/MessageStore';

const DashboardContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  '& .bp3-tabs': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  '& .bp3-tab-panel': {
    flex: 1,
  },
  '& .ag-theme-balham': {
    width: '100%',
  },
});

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

const CardStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const labelStyle = css({
  fontWeight: 'bold',
  marginRight: '5px',
  marginTop: '5px',
});

interface SecretariatDashboardProps extends WithTranslateProps {
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  BankHolidayStore?: Instance<typeof BankHolidayStore>;
  BankHolidayDateStore?: Instance<typeof BankHolidayDateStore>;
  GroupStore?: Instance<typeof GroupStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  PharmacyAnnualReviewStore?: Instance<typeof PharmacyAnnualReviewStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  MessageStore?: Instance<typeof MessageStore>;
}

interface SecretariatDashboardState {
  isLoading: boolean;
  secretariatId?: number;
  blackPointSystemStatus: Array<Instance<typeof SystemStatus>>;
  exchangeSystemStatus: Array<Instance<typeof SystemStatus>>;
  planningSystemStatus: Array<Instance<typeof SystemStatus>>;
  annualReviews: Array<Instance<typeof PharmacyAnnualReview>>;
  annualReviewSearchCriteria?: Instance<typeof PharmacyAnnualReviewSearchCriteria>;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  groupList: Array<Instance<typeof Group>>;
  missingBlackpointSearchCriteria: any;
  missingBlackpoint: any[];
  bankHolidayDateSearchCriteria: any;
  bankHolidayDates: any[];
  bankHolidayList: any[];
  messageList: Array<Instance<typeof Message>>;
  filter: any;
  activeFilter: 'validated' | 'calculated' | 'all';
  currentTab?: DashboardTab;
}

enum DashboardTab {
  Main = 'main',
  BlackPoint = 'blackPoint',
  Exchange = 'exchange',
  BankHoliday = 'bankHoliday',
  Planning = 'planning',
  AnnualReview = 'annualReview',
  Prefs = 'prefs',
  MissingBlackPoint = 'missingBlackPoint',
  Message = 'message',
  DutyUpload = 'dutyUpload',
  DutyDownload = 'dutyDownload',
  BankHolidayDates = 'bankHolidayDates',
}

@inject('ApplicationStore')
@inject('AuthenticationStore')
@inject('BankHolidayStore')
@inject('BankHolidayDateStore')
@inject('GroupStore')
@inject('SecretariatStore')
@inject('SystemStatusStore')
@inject('PharmacyAnnualReviewStore')
@inject('PharmacyStore')
@inject('PharmacyDetailedStore')
@inject('MessageStore')
@observer
class SecretariatDashboard extends React.Component<SecretariatDashboardProps, SecretariatDashboardState> {
  state = {
    isLoading: false,
    blackPointSystemStatus: [],
    exchangeSystemStatus: [],
    planningSystemStatus: [],
    annualReviews: [],
    pharmacyList: [],
    groupList: [],
    missingBlackpointSearchCriteria: null,
    missingBlackpoint: [],
    bankHolidayDateSearchCriteria: null,
    bankHolidayDates: [],
    bankHolidayList: [],
    messageList: [],
    filter: '',
    activeFilter: 'calculated',
    currentTab: undefined,
  } as SecretariatDashboardState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    const { AuthenticationStore, ApplicationStore, translate, SecretariatStore, GroupStore } = this.props;
    if (AuthenticationStore!.isSecretariat) {
      if (AuthenticationStore!.secretariatId === null) {
        ApplicationStore!.navigate(`${Page.Error}/${translate(TLCode.ERROR_NoSecretariatRelatedToThisUser)}`);
      } else {
        await this.setSecretariat(AuthenticationStore!.secretariatId);
      }
    } else if (AuthenticationStore!.isSuperAdmin && SecretariatStore!.selectedSecretariatId !== undefined) {
      await this.setSecretariat(SecretariatStore!.selectedSecretariatId);
    } else {
      await SecretariatStore!.checkListLoaded(); // loadList();
    }
    await GroupStore!.checkListLoaded(); // loadList();
    this._isMounted && this.setState({ isLoading: false });
  }

  async setSecretariat(secretariatId: number) {
    await this.loadPharmacyList();
    this._isMounted &&
      this.setState(
        {
          secretariatId,
          blackPointSystemStatus: [],
          exchangeSystemStatus: [],
          planningSystemStatus: [],
          annualReviews: [],
          missingBlackpointSearchCriteria: {
            secretariatId: this.state.secretariatId,
            year: moment().year() + 1,
            filter: 'UNCOMPLETE',
          },
          missingBlackpoint: [],
          messageList: [],
          bankHolidayDateSearchCriteria: {
            year: moment().year(),
          },
        },
        async () => {
          if (this.state.currentTab) await this.loadTabData(this.state.currentTab);
        },
      );
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadTabData = async (tabId: DashboardTab) => {
    switch (tabId) {
      case DashboardTab.Main:
        break;
      case DashboardTab.BlackPoint:
        await this.loadBlackPointSystemStatus();
        break;
      case DashboardTab.Exchange:
        await this.loadExchangeSystemStatus();
        break;
      case DashboardTab.BankHoliday:
        break;
      case DashboardTab.Planning:
        await this.loadPlanningSystemStatus();
        break;
      case DashboardTab.Prefs:
        await this.loadPharmacyList();
        break;
      case DashboardTab.AnnualReview:
        await this.loadAnnualReview();
        break;
      case DashboardTab.MissingBlackPoint:
        await this.loadMissingBlackPoint();
        break;
      case DashboardTab.Message:
        await this.loadPharmacyList();
        break;
      case DashboardTab.DutyUpload:
        break;
      case DashboardTab.DutyDownload:
        break;
      case DashboardTab.BankHolidayDates:
        await this.loadBankHolidayDates();
        await this.loadBankHolidayList();
        break;
    }
    this._isMounted && this.setState({ currentTab: tabId });
  };

  async loadBlackPointSystemStatus() {
    if (this.state.blackPointSystemStatus.length > 0) return;
    const criteria: any = { secretariatId: this.state.secretariatId, subSystem: SystemStatusSubSystem.BLACKPOINT };
    const response = await this.props.SystemStatusStore!.search(criteria);
    // @ts-ignore
    this._isMounted && this.setState({ blackPointSystemStatus: response });
  }

  async loadExchangeSystemStatus() {
    if (this.state.exchangeSystemStatus.length > 0) return;
    const criteria: any = { secretariatId: this.state.secretariatId, subSystem: SystemStatusSubSystem.EXCHANGE };
    const response = await this.props.SystemStatusStore!.search(criteria);
    this._isMounted &&
      this.setState({
        // @ts-ignore
        exchangeSystemStatus: response,
      });
  }

  async loadPlanningSystemStatus() {
    if (this.state.planningSystemStatus.length > 0) return;
    const criteria: any = { secretariatId: this.state.secretariatId, subSystem: SystemStatusSubSystem.PLANNING };
    const response = await this.props.SystemStatusStore!.search(criteria);
    this._isMounted &&
      this.setState({
        // @ts-ignore
        planningSystemStatus: response,
      });
  }

  async loadAnnualReview() {
    if (this.state.annualReviews.length > 0) return;
    const criteria: any = {
      secretariatId: this.state.secretariatId,
      year: moment().year() + 1,
      validated: false,
      status: 'Calculated',
      filter: 'all',
    };
    this.props.PharmacyAnnualReviewStore!.clearData();
    const response = await this.props.PharmacyAnnualReviewStore!.search(criteria);
    this._isMounted &&
      this.setState({
        // @ts-ignore
        annualReviews: response,
        annualReviewSearchCriteria: criteria,
      });
  }

  async loadMissingBlackPoint() {
    if (this.state.missingBlackpoint.length > 0) return;
    const criteria: any = {
      secretariatId: this.state.secretariatId,
      year: moment().year() + 1,
      filter: 'UNCOMPLETE',
    };
    this._isMounted &&
      this.setState({
        missingBlackpointSearchCriteria: criteria,
      });
    this.searchMissingBlackPoint();
  }

  async searchMissingBlackPoint() {
    const response = await this.props.PharmacyDetailedStore!.getMissingHolidays(
      this.state.missingBlackpointSearchCriteria,
    );
    this._isMounted &&
      this.setState({
        // @ts-ignore
        missingBlackpoint: response,
      });
  }

  navigateMissingBlackpoint = (direction) => {
    let searchCriteria = this.state.missingBlackpointSearchCriteria;
    searchCriteria.year = searchCriteria.year + direction;
    this.setState({ missingBlackpointSearchCriteria: searchCriteria });
    this.searchMissingBlackPoint();
  };

  async loadBankHolidayDates() {
    if (this.state.bankHolidayDates.length > 0) return;
    const criteria: any = {
      year: moment().year(),
    };
    this._isMounted &&
      this.setState({
        bankHolidayDateSearchCriteria: criteria,
      });
    this.searchBankHolidayDates();
  }

  async loadBankHolidayList() {
    if (this.state.bankHolidayList.length > 0) return;
    const criteria: any = { secretariatId: this.state.secretariatId, subSystem: SystemStatusSubSystem.BLACKPOINT };
    const response = await this.props.BankHolidayStore!.loadList();
    this._isMounted &&
      this.setState({
        // @ts-ignore
        bankHolidayList: response,
      });
  }

  async searchBankHolidayDates() {
    const response = await this.props.BankHolidayDateStore!.search(this.state.bankHolidayDateSearchCriteria);
    this._isMounted &&
      this.setState({
        // @ts-ignore
        bankHolidayDates: response,
      });
  }

  navigateBankHolidayDates = (direction) => {
    let searchCriteria = this.state.bankHolidayDateSearchCriteria;
    searchCriteria.year = searchCriteria.year + direction;
    this.setState({ bankHolidayDateSearchCriteria: searchCriteria });
    this.searchBankHolidayDates();
  };

  onMissingBlackPointSearch = async (searchData: any) => {
    const res: any = await this.props.PharmacyDetailedStore!.getMissingHolidays(
      this.state.missingBlackpointSearchCriteria,
    );
    this._isMounted &&
      this.setState({
        missingBlackpoint: res,
      });
  };

  loadPharmacyList = async () => {
    await this.props.PharmacyStore!.checkListLoaded(); //loadList();
    this._isMounted && this.setState({ pharmacyList: this.props.PharmacyStore!.dataList });
  };

  loadGroupList = async () => {
    const res: any = await this.props.GroupStore!.dataList;
    this._isMounted && this.setState({ groupList: res });
  };

  onSecretariatSelected = async (value: Instance<typeof Secretariat>) => {
    await this.setSecretariat(value.secretariatId);
  };

  onSaveStatus = async (data: any) => {
    await this.props.SystemStatusStore!.saveElement(data);
    await this.setSecretariat(this.state.secretariatId!);
  };

  onSaveBankHolidayDate = async (data: any) => {
    await this.props.BankHolidayDateStore!.saveElement(data);
  };

  onDeleteBankHolidayDate = async (data: any) => {
    const bhd = this.props.BankHolidayDateStore!.getById(data);
    this.props.BankHolidayDateStore!.deleteElement(bhd);
  };

  onValidateReview = async (data: any) => {
    await this.props.PharmacyAnnualReviewStore!.validateReview(data);
    const res: any = await this.props.PharmacyAnnualReviewStore!.search(this.state.annualReviewSearchCriteria!);
    this._isMounted &&
      this.setState({
        annualReviews: res,
      });
  };

  onAnnualReviewSearch = async (searchData: any) => {
    const res: any = await this.props.PharmacyAnnualReviewStore!.search(searchData);
    this._isMounted &&
      this.setState({
        annualReviews: res,
        annualReviewSearchCriteria: searchData,
      });
  };

  setFilterText = (filter) => {
    this.setState({
      filter,
    });
  };

  getSecretariatId = () => {
    if (this.props.AuthenticationStore!.isSuperAdmin)
      return this.props.SecretariatStore!.selectedSecretariatId === 0
        ? undefined
        : this.props.SecretariatStore!.selectedSecretariatId;
    if (this.props.AuthenticationStore!.isSecretariat) return this.props.AuthenticationStore!.secretariatId;
    return undefined;
  };

  showPharmacy = (pharmacy: any) => {
    return (
      (this.props.AuthenticationStore!.isSuperAdmin && this.props.SecretariatStore!.selectedSecretariatId === 0) ||
      (this.props.AuthenticationStore!.isSuperAdmin &&
        pharmacy !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId === pharmacy!.secretariatId) ||
      (pharmacy !== undefined &&
        this.props.AuthenticationStore!.isSecretariat &&
        this.props.AuthenticationStore!.secretariatId === pharmacy!.secretariatId)
    );
  };

  public render() {
    const { AuthenticationStore, SecretariatStore, ApplicationStore, GroupStore, translate } = this.props;
    const {
      secretariatId,
      blackPointSystemStatus,
      exchangeSystemStatus,
      planningSystemStatus,
      missingBlackpoint,
      bankHolidayDates,
      bankHolidayList,
      annualReviews,
      annualReviewSearchCriteria,
      isLoading,
      missingBlackpointSearchCriteria,
      bankHolidayDateSearchCriteria,
      pharmacyList,
      messageList,
      filter,
      activeFilter,
    } = this.state;

    return (
      <MainSectionLayout title={TLCode.SECRETARIAT_DASHBOARD_Title} isLoading={isLoading}>
        <Container>
          <Card className={CardStyle.toString()}>
            {/* {!AuthenticationStore!.isSecretariat && (
              <SecretariatSelectionContainer>
                <div className={labelStyle.toString()}>
                  <TranslatedMessage id={TLCode.SECRETARIAT} />
                </div>
                <SecretariatSelection
                  secretariatList={SecretariatStore!.dataList}
                  secretariatSelectedId={secretariatId}
                  getSecretariatById={SecretariatStore!.getById}
                  onChange={this.onSecretariatSelected}
                />
              </SecretariatSelectionContainer>
            )} */}
            {
              <DashboardContainer>
                <Tabs
                  id={DashboardTab.Main}
                  defaultSelectedTabId="level1"
                  onChange={this.loadTabData}
                  renderActiveTabPanelOnly={true}
                >
                  <Tab
                    id={DashboardTab.BlackPoint}
                    title={translate(TLCode.SECRETARIAT_DASHBOARD_BlackPointTitle)}
                    panel={
                      <BlackPointManager
                        blackPointSystemStatus={blackPointSystemStatus}
                        onStatusSave={this.onSaveStatus}
                        secretariatId={secretariatId}
                        navigate={ApplicationStore!.navigate}
                      />
                    }
                  />
                  <Tab
                    id={DashboardTab.Exchange}
                    title={translate(TLCode.SECRETARIAT_DASHBOARD_ExchangeTitle)}
                    panel={
                      <ExchangeManager
                        exchangeSystemStatus={exchangeSystemStatus}
                        onStatusSave={this.onSaveStatus}
                        secretariatId={secretariatId}
                      />
                    }
                  />
                  <Tab id={DashboardTab.BankHoliday} title={translate(TLCode.SECRETARIAT_DASHBOARD_BankHolidayTitle)} />
                  <Tab
                    id={DashboardTab.Planning}
                    title={translate(TLCode.SECRETARIAT_DASHBOARD_PlanningTitle)}
                    panel={
                      <PlanningManager
                        planningSystemStatus={planningSystemStatus}
                        onStatusSave={this.onSaveStatus}
                        secretariatId={secretariatId}
                      />
                    }
                  />
                  <Tab
                    id={DashboardTab.Prefs}
                    title={translate(TLCode.SECRETARIAT_DASHBOARD_PrefsTitle)}
                    panel={
                      <PrefManager
                        pharmacyList={pharmacyList.filter(this.showPharmacy)}
                        onReviewSave={this.onValidateReview}
                        searchCriteria={annualReviewSearchCriteria}
                        onSearch={this.onAnnualReviewSearch}
                        activeFilter={activeFilter}
                        setActiveFilter={(filter) => this.setState({ activeFilter: filter })}
                        filter={filter}
                        setFilterText={this.setFilterText}
                      />
                    }
                  />
                  <Tab
                    id={DashboardTab.AnnualReview}
                    title={translate(TLCode.SECRETARIAT_DASHBOARD_AnnualReviewTitle)}
                    panel={
                      <AnnualReviewManager
                        annualReviews={annualReviews}
                        onReviewSave={this.onValidateReview}
                        searchCriteria={annualReviewSearchCriteria}
                        onSearch={this.onAnnualReviewSearch}
                        activeFilter={activeFilter}
                        setActiveFilter={(filter) => this.setState({ activeFilter: filter })}
                        filter={filter}
                        setFilterText={this.setFilterText}
                        secretariatId={this.getSecretariatId()}
                      />
                    }
                  />
                  <Tab
                    id={DashboardTab.MissingBlackPoint}
                    title={translate(TLCode.MISSING_BLACKPOINTS_Title)}
                    panel={
                      <MissingBlackPointManager
                        missingBlackPoint={missingBlackpoint}
                        searchCriteria={missingBlackpointSearchCriteria}
                        onSearch={this.onMissingBlackPointSearch}
                        onChangeCriteria={this.navigateMissingBlackpoint}
                        pharmacyList={pharmacyList.filter(this.showPharmacy)}
                        navigate={ApplicationStore!.navigate}
                      />
                    }
                  />
                  <Tab
                    id={DashboardTab.Message}
                    title={translate(TLCode.MESSAGE_TabTitle)}
                    panel={
                      <MessageTab
                        secretariatId={this.getSecretariatId()}
                        pharmacyList={pharmacyList.filter(this.showPharmacy)}
                        groupList={GroupStore!.dataList}
                      />
                    }
                  />
                  <Tab id={DashboardTab.DutyUpload} title={translate(TLCode.DUTYUPLOAD_Title)} panel={<DutyUpload />} />
                  <Tab
                    id={DashboardTab.DutyDownload}
                    title={translate(TLCode.DUTYDOWNLOAD_Title)}
                    panel={<DutyDownload />}
                  />
                  {AuthenticationStore!.isSuperAdmin && (
                    <Tab
                      id={DashboardTab.BankHolidayDates}
                      title={translate(TLCode.BANK_HOLIDAY_DATES_Title)}
                      panel={
                        <BankHolidayDateManager
                          bankHolidayList={bankHolidayList}
                          bankHolidayDateList={bankHolidayDates}
                          searchCriteria={bankHolidayDateSearchCriteria}
                          onBankHolidayDateSave={this.onSaveBankHolidayDate}
                          onBankHolidayDateDelete={this.onDeleteBankHolidayDate}
                          onChangeCriteria={this.navigateBankHolidayDates}
                        />
                      }
                    />
                  )}
                </Tabs>
              </DashboardContainer>
            }
          </Card>
        </Container>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(SecretariatDashboard);
